import * as React from 'react';
import { BoostEligibilityInput, BoostEligibilitySelectionType } from '@frontend/applications/AffiliatesApp/types/globalTypes';
import { TOfferMember } from '@affiliates/components/MemberTable/types';
import { Info } from 'lucide-react';
import { Select as RevfluenceSelect } from '@revfluence/fresh';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shadcn/components/ui/tooltip';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/shadcn/components/ui/select';

interface BoostEligibilityProps {
  boostEligibility: BoostEligibilityInput | null;
  onChange: (val: BoostEligibilityInput | null) => void;
  offerMembers: TOfferMember[];
}

const ELIGIBILITY_OPTIONS = [
  { value: BoostEligibilitySelectionType.ALL, label: 'All members' },
  { value: BoostEligibilitySelectionType.INCLUDE, label: 'Specific Members' },
  { value: BoostEligibilitySelectionType.EXCLUDE, label: 'Excluding Members' },
  // TODO: Advanced selection can be added later
];

export function BoostEligibility({ boostEligibility, onChange, offerMembers }: BoostEligibilityProps) {
  const eligibilityType = boostEligibility?.eligibilityType || BoostEligibilitySelectionType.ALL;
  const selectedIds = boostEligibility?.memberIds || [];

  // Handlers
  function handleEligibilityTypeChange(val: BoostEligibilitySelectionType) {
    if (val === BoostEligibilitySelectionType.ALL) {
      onChange({ eligibilityType: val, memberIds: null, id: boostEligibility?.id ? Number(boostEligibility?.id) : null });
      onChange({ eligibilityType: val, memberIds: null, id: boostEligibility?.id ? Number(boostEligibility?.id) : null });
    } else {
      onChange({ eligibilityType: val, memberIds: [], id: boostEligibility?.id ? Number(boostEligibility?.id) : null });
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Inline eligibility type row */}
      <div className="flex items-center gap-2 text-sm">
        <span>Boost applies to</span>
        <Select value={eligibilityType} onValueChange={handleEligibilityTypeChange}>
          <SelectTrigger className="w-auto min-w-[120px] h-8 px-2 text-sm border-grey-3">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="z-[9999]">
            {ELIGIBILITY_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value} className="text-sm">
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span>in the offer</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <Info className="ml-1 w-4 h-4 text-muted-foreground cursor-pointer" />
          </TooltipTrigger>
          <TooltipContent>
            Control which members are eligible for this boost.
          </TooltipContent>
        </Tooltip>
      </div>
      {/* Tag-input for INCLUDE/EXCLUDE */}
      {(eligibilityType === BoostEligibilitySelectionType.INCLUDE || eligibilityType === BoostEligibilitySelectionType.EXCLUDE) && (
        <div className="flex flex-col gap-1 relative z-[50]">
          <RevfluenceSelect
            placeholder="Search and select members"
            mode="multiple"
            showSearch
            filterOption={(input, option) => String(option?.label).toLowerCase().includes(input.toLowerCase())}
            notFoundContent={offerMembers.length === 0 ? 'No members found' : null}
            allowClear
            style={{ width: '100%' }}
            dropdownStyle={{ zIndex: 9999, pointerEvents: 'auto', userSelect: 'text' }}
            options={offerMembers.map((m) => ({ label: m.name, value: m.memberId }))}
            value={selectedIds}
            onChange={(ids) => onChange({
              ...boostEligibility,
              eligibilityType,
              memberIds: ids,
              id: boostEligibility?.id ?? null,
            })}
            open
          />
        </div>
      )}
    </div>
  );
}
